import type { OnboardingFeaturedTemplateRef } from "@/constants/onboardingFeaturedTemplates";
import type { TripTemplate } from "@/types/tripTemplates";

type SnapshotSource = {
  title: string;
  destination: string;
  duration: number;
  shortDescription: string;
  coverImage: string;
  difficulty: TripTemplate["difficulty"];
  travelStyle: TripTemplate["travelStyle"];
  tags: string[];
  topicTags: string[];
  bestMonths: string[];
  estimatedBudgetMin: number;
  estimatedBudgetMax: number;
  currency: string;
};

const SNAPSHOTS: Record<string, SnapshotSource> = {
  "nyc-world-cup-weekend": {
    title: "NYC World Cup Weekend",
    destination: "New York City, USA",
    duration: 3,
    shortDescription: "Walkable art, skyline views, and food finds across Manhattan and Brooklyn",
    coverImage:
      "https://images.unsplash.com/photo-1518391846015-55a9cc003b25?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    difficulty: "moderate",
    travelStyle: ["urban", "food", "cultural", "solo"],
    tags: ["world-cup-2026", "solo-friendly", "nightlife", "foodie", "museums"],
    topicTags: ["world-cup-2026", "solo-friendly", "nightlife", "foodie", "museums"],
    bestMonths: ["Apr", "May", "Sep", "Oct", "Dec", "Jun", "Jul", "Aug"],
    estimatedBudgetMin: 900,
    estimatedBudgetMax: 1400,
    currency: "USD",
  },
  "calgary-stampede-banff-5-days": {
    title: "Calgary Stampede + Banff (5 days)",
    destination: "Calgary, Canada",
    duration: 5,
    shortDescription: "Experience Alberta's two biggest attractions in one trip.",
    coverImage:
      "https://images.unsplash.com/photo-1561134643-668f9057cce4?q=80&w=1674&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    difficulty: "moderate",
    travelStyle: ["cultural", "family", "solo", "road-trip", "romantic", "nature", "food", "adventure"],
    tags: ["calgary", "food", "live-music", "rodeo", "festival", "calgary-stampede"],
    topicTags: ["calgary", "live-music", "rodeo", "festival", "calgary-stampede"],
    bestMonths: ["Jul"],
    estimatedBudgetMin: 3188,
    estimatedBudgetMax: 4688,
    currency: "USD",
  },
  "america-250-3-days-in-dc": {
    title: "America 250: 3 Days in DC",
    destination: "Washington, DC, USA",
    duration: 3,
    shortDescription: "Smithsonian hits, monuments by night, and easy family dining",
    coverImage:
      "https://images.unsplash.com/photo-1617581629397-a72507c3de9e?q=80&w=1472&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    difficulty: "moderate",
    travelStyle: ["family", "cultural", "urban", "budget", "solo", "food"],
    tags: ["america-250", "family-friendly", "museum", "budget", "walkable"],
    topicTags: ["america-250", "family-friendly", "museum", "walkable"],
    bestMonths: ["Mar", "Apr", "May", "Sep", "Oct"],
    estimatedBudgetMin: 1530,
    estimatedBudgetMax: 2250,
    currency: "USD",
  },
};

export function bundledOnboardingFeaturedTemplate(
  ref: OnboardingFeaturedTemplateRef,
): TripTemplate {
  const snapshot = SNAPSHOTS[ref.slug];
  const now = new Date();

  return {
    id: ref.id,
    title: snapshot.title,
    destination: snapshot.destination,
    duration: snapshot.duration,
    shortDescription: snapshot.shortDescription,
    coverImage: snapshot.coverImage,
    difficulty: snapshot.difficulty,
    travelStyle: snapshot.travelStyle,
    estimatedBudget: {
      min: snapshot.estimatedBudgetMin,
      max: snapshot.estimatedBudgetMax,
      currency: snapshot.currency,
      breakdown: {
        flights: 0,
        lodging: 0,
        food: 0,
        activities: 0,
        transport: 0,
      },
    },
    itinerary: [],
    bestMonths: snapshot.bestMonths,
    tags: snapshot.tags,
    featured: true,
    usageCount: 0,
    highlights: [],
    createdBy: "system",
    status: "approved",
    createdAt: now,
    updatedAt: now,
    topicTags: snapshot.topicTags,
  } as TripTemplate;
}
